import { API, APIResponse } from 'Shared/services/apiInterface';
import { SwrKeys } from 'Shared/services/swr/swrKeys';
import useSWR from 'swr';

/**
 * Retrieve endpoint from UCM
 * @param documentName
 * @param version
 */
const getAgreementDocument = (documentName: string, version = 'latest'): Promise<APIResponse> => {
  const promise = API({
    method : 'GET',
    baseURL: process.env.REACT_APP_UCM_BASE_URL,
    url    : `v1/apps/1/docs/${documentName}/versions/${version}`,
  });

  return promise;
};

/**
 * Retrieves an agreement document, preferring cached data if available.
 *
 * This function checks if the specified document is available in the `STATIC_DATA_PRIVACY` or `STATIC_DATA_TERMS` environment variable.
 * If found, it returns the cached data. Otherwise, it falls back to fetching the data from the API.
 *
 * @param {string} documentName - The name of the document to fetch.
 * @param {string} [version='latest'] - The version of the document, defaults to 'latest'.
 * @returns {Promise<APIResponse | { data: any }>} - Promise resolving to either the cached document or the API response.
 */
const getCachedOrApiDocument = (documentName: string, version = 'latest'): Promise<APIResponse | { data: any }> => {
  let cachedDocument;

  if (documentName === SwrKeys.PrivacyPolicy) {
    cachedDocument = process.env.STATIC_DATA_PRIVACY;
  } else if (documentName === SwrKeys.TermsAndConditions) {
    cachedDocument = process.env.STATIC_DATA_TERMS;
  }

  if (cachedDocument) {
    return Promise.resolve({ data: cachedDocument });
  }

  return getAgreementDocument(documentName, version);
};

/**
 *
 * @returns {SWRResponse<UserApiGetModel, any>}
 */
const useGetAgreementDocument = (documentName: string, version = 'latest') => {
  return useSWR([SwrKeys.AgreementDocument, documentName, version], () => getCachedOrApiDocument(documentName, version));
};

export {
  getAgreementDocument,
  getCachedOrApiDocument,
  useGetAgreementDocument
};